<template>
  <div class="app-members-selector">
    <div
      class="app-select-wrapper"
      @click="show"
    >
      <div class="d-flex align-items-end justify-content-between">
        <div class="app-members-selector-title">
          <slot name="title" />
          <slot name="subtitle" />
        </div>

        <a
          v-if="showSelectAllTrigger && !disabled"
          class="mb-5 select-all"
          href="#"
          @click.prevent="handleSelectAll"
        >
          {{ selectAllLabel }}
        </a>
      </div>

      <div
        class="app-select"
        data-testid="members-selector-trigger"
      >
        <div
          class="select2 select2-container select2-container--bootstrap"
          data-select2-ignore
        >
          <div class="selection">
            <div
              v-if="loading"
              class="select2-selection select2-selection-fake"
            >
              {{ loadingMessage }}
            </div>
            <template v-else>
              <div
                v-if="totalSelectionCount"
                class="select2-selection-counter d-flex align-items-center"
              >
                {{ totalSelectionsLabel }}
              </div>
              <div
                v-else
                class="select2-selection select2-selection-ready select2-selection-fake"
                :class="{ 'select2-selection-fake-disabled': disabled }"
              >
                {{ placeholder }}
              </div>
            </template>
          </div>
        </div>
      </div>

      <div
        v-if="!!$slots.footnote"
        class="app-members-selector-footnote"
      >
        <slot name="footnote" />
      </div>
    </div>

    <AppDialog
      class="members-selection-dialog"
      :show="isReady"
      :title="title"
      size="lg"
      append-to-body
      @close="close"
    >
      <AppTabs
        v-if="records.length"
        compact
        :selected-tab="selectedTab"
      >
        <AppTab
          v-for="record in records"
          :key="`${record}-tab`"
          :label="record"
          :tab-id="record"
          @tab-selected="selectTab(record)"
        />
      </AppTabs>

      <AppMembersSelection
        v-for="record in records"
        v-show="selectedTab === record"
        :key="`${record}-selection`"
        :visible="isReady && selectedTab === record"
        :query="query"
        :multiple="multiple"
        :sortable="sortable"
        :members="items[record]"
        :changed-members="changedMembers[record]"
        :pagination="pagination"
        :select-all-button-enabled="selectAllButtonEnabled"
        :selections-count="selectionsCount[record]"
        :loading="loading"
        :fetching="fetching"
        :records-name="record"
        :search-placeholder="searchPlaceholders[record]"
        @toggle-selection="toggleSelection"
        @search="search"
        @update="fetch"
        @select-all="handleSelectAll"
        @clear-all="handleClearAll"
        @sort="sort"
      />

      <template #footer>
        <button
          v-if="showSubmitButton"
          class="btn btn-primary mr-0"
          type="submit"
          @click.prevent="apply"
        >
          {{ submitButtonLabel }}
        </button>
      </template>
    </AppDialog>
  </div>
</template>

<script>
import AppMembersSelection from '~/components/AppMembersSelection.vue';
import { isEmpty, cloneDeep } from 'lodash-es';
import { pluralize } from '~/utils/string_utils';

export default {
  name: 'AppMembersSelector',
  components: {
    AppMembersSelection,
  },
  props: {
    organizationId: {
      type: Number,
      required: true,
    },
    resourceId: {
      type: [Number, null],
      default: null,
    },
    resource: {
      type: [Function, Object],
      required: true,
    },
    changedItems: {
      type: Object,
      default: () => ({}),
    },
    selectAllItems: {
      type: Object,
      default: () => ({}),
    },
    records: {
      type: Array,
      default: () => ['members'],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    sortable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showSelectAllTrigger: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Members',
    },
    placeholder: {
      type: String,
      default: 'Select members',
    },
    loadingMessage: {
      type: String,
      default: 'Loading, please wait...',
    },
    selectAllLabel: {
      type: String,
      default: 'Select all',
    },
    role: {
      type: String,
      default: 'user',
    },
    recordsName: {
      type: String,
      default: 'members',
    },
    submitButtonLabel: {
      type: String,
      default: 'Save',
    },
    searchPlaceholder: {
      type: String,
      default: 'Search members',
    },
    action: {
      type: String,
      default: '',
      validator(value) {
        return ['filter', ''].includes(value);
      },
    },
  },
  data: () => ({
    members: [],
    initialMembers: [],
    changedMembers: {},
    changedMembersInitial: {},
    pagination: {},
    selectedCount: 0,
    showDialog: false,
    selectAll: {},
    selectAllInitial: {},
    clearAll: false,
    clearAllInitial: false,
    selectionsCount: {},
    selectionsCountInitial: {},
    firstFetch: {},
    firstFetchInitial: {},
    loading: false,
    query: '',
    selectedTab: '',
    items: {},
    showSubmitButton: false,
    fetching: false,
  }),
  computed: {
    isReady() {
      return this.showDialog && (!this.loading || !this.firstFetch[this.selectedTab]);
    },
    multiTabs() {
      return this.records.length > 1;
    },
    totalSelectionCount() {
      return this.records.reduce((accumulator, record) => accumulator + this.selectionsCount[record], 0);
    },
    totalSelectionsLabel() {
      const string = this.multiTabs ? 'item' : this.recordsName.substring(0, this.recordsName.length - 1);

      return `${this.totalSelectionCount} ${pluralize(this.totalSelectionCount, string)}`;
    },
    selectAllButtonEnabled() {
      return this.selectAll[this.selectedTab] && this.changedMembers[this.selectedTab].length === 0;
    },
    membersArray() {
      return Object.fromEntries(
        this.records.map((record) => [
          record,
          this.changedMembers[record].map((m) => ({
            user_id: m.id,
            selected: m.selected,
            role: this.role,
            name: m.name,
          })),
        ]),
      );
    },
    searchPlaceholders() {
      return Object.fromEntries(this.records.map((record) => [record, `Search ${record}`]));
    },
  },
  created() {
    this.reset();
  },
  methods: {
    async fetch({ page = 1, sort = 'name,asc' }) {
      try {
        this.fetching = true;

        const queryParams = this.prepareQueryParams(page, sort);
        const response = this.multiTabs
          ? await this.resource[this.selectedTab]({ organization_id: this.organizationId }, queryParams.toString(), {
              abortReload: true,
            })
          : await this.resource({ organization_id: this.organizationId }, queryParams.toString(), {
              abortReload: true,
            });

        if (response && response[this.selectedTab] && response.pagination) {
          this.members = response[this.selectedTab] || [];
          // Clone the raw response — `structuredClone` throws DataCloneError on Vue 3 reactive Proxies.
          this.initialMembers = structuredClone(response[this.selectedTab] || []);
          this.pagination = response.pagination;
          this.selectedCount = response.selected_count;

          this.items[this.selectedTab] = response[this.selectedTab] || [];
          this.showSubmitButton = this.items[this.selectedTab].length > 0;

          if (this.multiple) {
            // For multi-select scenarios:
            // Whenever we fetch new members (first arriving on the list or fetching a new page),
            // we need to take into account that we may have made selection changes _in another
            // page_ of the list. We need to evaluate them and update this.members accordingly,
            // otherwise we would lose them.
            //
            // - We select 3 members on page 1
            // - We move to page 2 (fetch)
            // - We move to page 1 again (fetch)
            //
            // If we don't do anything, the 3 selected members would be gone, since we have
            // re-fetched the list. This is why we need to _merge the selections_ with what we
            // have fetched.

            this.checkSelectionsInFetchedMembers();

            if (this.firstFetch[this.selectedTab]) {
              this.setTrueSelectionsCount();

              this.firstFetch[this.selectedTab] = false;
            }
          } else {
            this.changedMembers[this.selectedTab] = [];
            this.setTrueSelectionsCount();
          }
        } else {
          throw new Error('Unable to fetch data for the members selection dialog.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage(error?.responseJSON?.alert || 'An error has occurred');
        }
      } finally {
        this.fetching = false;
      }
    },
    prepareQueryParams(page, sort) {
      const params = {
        page,
        sort,
        role: this.role,
        query: this.query,
        resource_id: this.resourceId,
      };
      // This will prevent from sending empty params at all, cleaning the URL in the process.
      const cleanedParams = Object.fromEntries(
        Object.entries(params).filter(([, value]) => !['', null, undefined].includes(value)),
      );
      const queryParams = new URLSearchParams(cleanedParams);

      queryParams.append('options[clear_all]', this.clearAll);
      queryParams.append('options[select_all]', this.selectAll[this.selectedTab]);

      if (this.action) {
        queryParams.append('options[action]', this.action);
      }

      const changedItems = this.multiTabs ? 'changed_items' : 'changed_members';

      this.membersArray[this.selectedTab].forEach((member) => {
        queryParams.append(`${changedItems}[][user_id]`, member.user_id);
        queryParams.append(`${changedItems}[][selected]`, member.selected);
      });

      return queryParams;
    },
    checkSelectionsInFetchedMembers() {
      // If 'Select all' is active, we have to make that apply on the current page, too.
      if (this.selectAll[this.selectedTab] === true) {
        this.updateListsAfterSelectAllEvent();

        return;
      }

      // Iterate over each member of this page and check if we have previously toggled its
      // original selection (stored in this.changedMembers).
      this.members.forEach((member) => {
        const found = this.findInChangedMembers(member);

        if (found) {
          member.selected = found.selected;
        }
      });
    },
    setTrueSelectionsCount() {
      this.selectionsCount[this.selectedTab] = this.pagination?.selected_count || this.selectedCount || 0;
    },
    toggleSelection(member) {
      const target = this.members.find((m) => m.id === member.id);

      if (target) {
        if (this.multiple) {
          target.selected = !target.selected;

          this.handleChangedMember(target);
          this.updateSelectionsCount(target);
        } else {
          // Remove prior selections and select target member, then rebuild the list of
          // changed members with it.
          this.members.forEach((m) => {
            m.selected = !!(m.id === target.id && !target.selected);
          });

          this.changedMembers[this.selectedTab] = [target];
        }
      }
    },
    clearMatchingSelections(changedMembers, selectAll = false) {
      if (selectAll) {
        this.members.forEach((member) => {
          if (member.selected && !changedMembers.some((cm) => cm.user_id === member.id)) {
            this.toggleSelection(member);
          }
        });
      } else {
        changedMembers.forEach((changedMember) => {
          const target = this.members.find(
            (m) => m.id === changedMember.user_id && m.selected && changedMember.selected,
          );

          if (target) {
            this.toggleSelection(target);
          }
        });
      }
    },
    findInChangedMembers(member) {
      return this.changedMembers[this.selectedTab].find((m) => m.id === member.id);
    },
    handleChangedMember(member) {
      const found = this.findInChangedMembers(member);

      // If we have the member on the list, just update its selected state. Otherwise, add it in.
      if (found) {
        // If 'Select all' is on and the member is selected back, or 'Select all' is off and the member is unselected back,
        // then remove him from the list of changed members
        if ((this.selectAll[this.selectedTab] && member.selected) || !member.selected) {
          this.changedMembers[this.selectedTab] = this.changedMembers[this.selectedTab].filter(
            (changedMember) => ![found].includes(changedMember),
          );
        } else {
          found.selected = member.selected;
        }
      } else {
        this.changedMembers[this.selectedTab].push(member);
      }
    },
    updateSelectionsCount(member) {
      const count = this.selectionsCount[this.selectedTab];

      this.selectionsCount[this.selectedTab] = member.selected ? count + 1 : count - 1;
    },
    handleSelectAll() {
      if (!this.loading) {
        this.clearAll = false;
        this.selectAll[this.selectedTab] = true;
        this.changedMembers[this.selectedTab] = [];
        this.updateListsAfterSelectAllEvent();
      }
    },
    async handleClearAll() {
      if (!this.loading) {
        // If we have an active query filter, we need to clear it and re-fetch the "clean" list.
        // The search() method does exactly that, so we'll just call it without any parameters.
        if (this.query) {
          await this.search();
        }

        // If "clear all" is true, it's not just a matter of removing all selections. We also
        // have to send the flag to the BE, because, just like with "select all", we will not
        // have access to members outside of the current page. The BE needs a way to remove all
        // the selections, so we send the flag as well as any member changes that come afterwards.

        this.selectAll[this.selectedTab] = false;
        this.clearAll = true;
        this.selectionsCount[this.selectedTab] = 0;

        this.forceSelectionsInCurrentPage(false);
      }
    },
    updateListsAfterSelectAllEvent() {
      // If "select all" is true, we have to pretend that all available members are selected,
      // even in other pages. This isn't true (we haven't fetched them all), but we will let
      // the back-end know we are "selecting all" and every member will be taken into account.
      if (this.selectAll[this.selectedTab]) {
        // Don't set the member count to the total headcount of the organization if some members were unselected
        // (i.e. the list of changed members isn't empty)
        if (this.changedMembers[this.selectedTab].length === 0) {
          this.selectionsCount[this.selectedTab] = this.pagination.total_count;
        }
      } else {
        this.selectionsCount[this.selectedTab] = 0;
      }
      this.forceSelectionsInCurrentPage(this.selectAll[this.selectedTab]);
    },
    forceSelectionsInCurrentPage(selected = true) {
      this.members.forEach((m) => {
        const changedMember = this.findInChangedMembers(m);

        if (changedMember && this.selectAll[this.selectedTab]) {
          m.selected = changedMember.selected;
        } else {
          m.selected = selected;
        }
      });

      // If 'select all' is true, add/update deselected members in the list of changed members
      if (this.selectAll[this.selectedTab]) {
        const deselectedMembers = this.members.filter((m) => m.selected === false);

        this.changedMembers[this.selectedTab] = [
          ...this.changedMembers[this.selectedTab].filter(
            (cm) => !deselectedMembers.map((dm) => dm.id).includes(cm.id),
          ),
          ...deselectedMembers,
        ];
      } else if (this.clearAll) {
        this.changedMembers[this.selectedTab] = [];
      } else {
        this.changedMembers[this.selectedTab] = [...this.members];
      }
    },
    async search(query = '') {
      this.query = query;
      await this.fetch({ page: 1 });
    },
    async sort(page, sort = 'name,asc') {
      this.query = '';
      await this.fetch({ page, sort });
    },
    show() {
      if (!this.loading && !this.disabled) {
        this.showDialog = true;
      }
    },
    hide() {
      this.showDialog = false;
    },
    async close() {
      // Discard state changes when user presses "x" (close)
      this.changedMembers = cloneDeep(this.changedMembersInitial);
      this.selectAll = cloneDeep(this.selectAllInitial);
      this.clearAll = cloneDeep(this.clearAllInitial);
      this.firstFetch = cloneDeep(this.firstFetchInitial);
      this.selectionsCount = cloneDeep(this.selectionsCountInitial);

      await this.fetch({ page: 1 });

      this.$emit('close');
      this.hide();
    },
    async reset() {
      this.selectedTab = this.records[0];

      this.changedMembersInitial = isEmpty(this.changedItems) ? this.populate([]) : this.changedItems;
      this.selectAllInitial = isEmpty(this.selectAllItems) ? this.populate(false) : this.selectAllItems;
      this.firstFetchInitial = this.populate(true);
      this.selectionsCountInitial = isEmpty(this.changedItems)
        ? this.populate(0)
        : Object.fromEntries(this.records.map((record) => [record, this.changedItems[record].length]));

      this.changedMembers = isEmpty(this.changedItems) ? this.populate([]) : this.changedItems;
      this.selectAll = isEmpty(this.selectAllItems) ? this.populate(false) : this.selectAllItems;
      this.firstFetch = this.populate(true);
      this.selectionsCount = isEmpty(this.changedItems)
        ? this.populate(0)
        : Object.fromEntries(this.records.map((record) => [record, this.changedItems[record].length]));

      await this.fetch({ page: 1 });
    },
    populate(value) {
      return Object.fromEntries(this.records.map((record) => [record, value]));
    },
    apply() {
      // Create a copy of the selector state in case the user opens the selector again, does something
      // and abandons the change by clicking "x" (close)
      this.changedMembersInitial = cloneDeep(this.changedMembers);
      this.selectAllInitial = cloneDeep(this.selectAll);
      this.clearAllInitial = cloneDeep(this.clearAll);
      this.firstFetchInitial = cloneDeep(this.firstFetch);
      this.selectionsCountInitial = cloneDeep(this.selectionsCount);

      if (this.multiTabs) {
        const options = {
          select_all: this.selectAll,
          clear_all: !!this.clearAll,
          role: this.role,
        };

        this.$emit('submit-selections', {
          items: this.membersArray,
          options,
          selectionCount: this.selectionsCount,
        });
      } else {
        const options = {
          select_all: this.selectAll[this.selectedTab],
          clear_all: !!this.clearAll,
          role: this.role,
        };

        this.$emit('submit-selections', {
          members: this.membersArray[this.selectedTab],
          options,
          selectionCount: this.selectionsCount[this.selectedTab],
          initialMembers: this.initialMembers,
        });
      }

      this.hide();
    },
    selectTab(tab) {
      if (this.selectedTab !== tab) {
        this.query = '';
        this.selectedTab = tab;
        this.fetch({ page: 1 });
      }
    },
  },
};
</script>
